import { useState } from 'react';
import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { ArrowRight } from 'lucide-react';

import { AuthButton } from '../../components/auth/AuthButton';
import { AuthDivider } from '../../components/auth/AuthDivider';
import { AuthInput } from '../../components/auth/AuthInput';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { EarlyAccessCard } from '../../components/auth/EarlyAccessCard';
import { SocialLoginButton } from '../../components/auth/SocialLoginButton';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { handleGoogleLogin } from '../../services/oauth';
import { userReducer } from '../../store/reducers/user.reducer';
import { getLoginError } from '../../store/selectors/user.selector';
import { history } from '../../store/store';

const { login } = userReducer.actions;

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');

  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const loginError = useAppSelector(getLoginError);

  // Google sign-in for an email without an account comes back here; registration is invite-only.
  const notInvited = searchParams.get('error') === 'registration_disabled';

  const validateEmail = (value: string) => {
    if (!value) {
      setEmailError('Please provide your email');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateEmail(email)) dispatch(login({ email, password }));
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back to TAMAN."
      align="start"
      cardClassName="md:max-w-[440px] md:px-7 md:py-6"
      logoBadge={
        <span className="flex items-center gap-1.5 whitespace-nowrap text-[10px] font-medium tracking-[0.16em] uppercase text-green01">
          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-green01" aria-hidden="true" />
          Early access
        </span>
      }
    >
      <EarlyAccessCard notInvited={notInvited} initialOpen={notInvited || searchParams.get('subscribe') === '1'} />

      <AuthDivider label="Already have access?" />

      <SocialLoginButton provider="google" onClick={handleGoogleLogin} variant="neutral" />

      <AuthDivider label="or sign in with email" variant="plain" />

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <AuthInput
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          error={emailError || (loginError ? ' ' : undefined)}
          placeholder="you@company.com"
          autoComplete="email"
          name="email"
        />

        <AuthInput
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          error={
            loginError
              ? 'Invalid email or password. No account yet? TAMAN is invite-only, so subscribe above.'
              : undefined
          }
          autoComplete="current-password"
          name="password"
        />

        <div className="flex items-center justify-between text-xs">
          <label className="flex items-center gap-1.5 py-2 -my-2 text-greyP2 cursor-pointer">
            <input type="checkbox" id="remember-me" className="w-3.5 h-3.5 accent-green01" />
            Remember me
          </label>
          <button
            type="button"
            onClick={() => history.push('/reset-password')}
            className="py-2 -my-2 text-green01 hover:underline cursor-pointer"
          >
            Forgot password?
          </button>
        </div>

        <AuthButton type="submit" variant="light">
          Sign in <ArrowRight size={16} aria-hidden="true" />
        </AuthButton>
      </form>
    </AuthLayout>
  );
};
